import { Vector2 } from '@seddi/three';
import type { Dispatch } from 'redux';

import type { MockupLogo } from '../../types/mockupLogos';
import { updateGraphicTransform } from '../../store/appSlice';
import type { Raycaster } from '../../viewer/viewerRaycaster';

type Transform0 = {
  logoId: string;
  meshId: string;
  translation: Vector2;
  translationOffset: Vector2;
};

const DRAG_THRESHOLD = 0.005;

export class TransformControls {
  readonly canvas: HTMLElement;
  readonly raycaster: Raycaster;
  readonly dispatch: Dispatch;

  enabled = true;

  logos: MockupLogo[] = [];
  selection: string | undefined;

  isDown = false;
  isDrag = false;

  onPointerDown: (event: PointerEvent) => void;
  onPointerMove: (event: PointerEvent) => void;

  constructor(canvas: HTMLElement, raycaster: Raycaster, dispatch: Dispatch) {
    this.canvas = canvas;
    this.raycaster = raycaster;
    this.dispatch = dispatch;

    this.transform0 = {
      logoId: '',
      meshId: '',
      translation: new Vector2(),
      translationOffset: new Vector2(),
    };

    const scope = this;

    this.onPointerDown = function (event: PointerEvent) {
      if (event.button !== 0) return; // Ignore right-click; logos never interact on right-click

      const { logos, selection, raycaster, transform0 } = scope;
      const { hit } = raycaster;

      if (hit.logoId && selection === hit.logoId) {
        scope.isDown = true;
        scope.pointerDown.copy(scope.pointer);

        transform0.logoId = hit.logoId;
        transform0.meshId = hit.meshId ?? '';
        const logo = logos.find(l => l.id === hit.logoId);
        if (logo) {
          transform0.translation.set(logo.transform.translation.x, logo.transform.translation.y);
          transform0.translationOffset.copy(transform0.translation).sub(hit.uv);
        }

        canvas.setPointerCapture(event.pointerId);
        canvas.addEventListener('pointerup', onPointerUp);
      }
    };

    this.onPointerMove = function (event: PointerEvent) {
      if (!scope.isDown) return;

      scope.pointer.x = (event.offsetX / canvas.clientWidth) * 2 - 1;
      scope.pointer.y = -(event.offsetY / canvas.clientHeight) * 2 + 1;

      if (scope.isDown && !scope.isDrag) {
        scope.isDrag = scope.pointerDown.distanceTo(scope.pointer) > DRAG_THRESHOLD;
      }

      if (!scope.isDrag) return;

      const { logos, transform0 } = scope;
      const hit = scope.raycaster.hit;

      const logo = logos.find(l => l.id === transform0.logoId);
      if (!logo) return;

      const translationMove = new Vector2();

      if (hit.meshId === transform0.meshId) {
        translationMove.copy(transform0.translationOffset).add(hit.uv);
      } else {
        translationMove.copy(transform0.translation);
      }

      const { translation } = logo.transform;
      if (
        Math.abs(translationMove.x - translation.x) > 1e-6 ||
        Math.abs(translationMove.y - translation.y) > 1e-6
      ) {
        dispatch(
          updateGraphicTransform({
            id: transform0.logoId,
            translation: { x: translationMove.x, y: translationMove.y },
          }),
        );
      }
    };

    function onPointerUp(event: PointerEvent) {
      try {
        canvas.releasePointerCapture(event.pointerId);
      } catch {
        // Capture may have been released by PickControls
      }
      canvas.removeEventListener('pointerup', onPointerUp);
      scope.isDown = false;
      scope.isDrag = false;
    }
  }

  dispose() {}

  private readonly pointer = new Vector2();
  private readonly pointerDown = new Vector2();
  private transform0: Transform0;
}
